import {
  type BigDecimal,
  createBigDecimal,
  plus,
  toStringBigDecimal,
} from '@0xtorch/big-decimal'
import { z } from 'zod'

/** 期間集計で扱う損益列。計算と既存データの補完で同じ列を加算する。 */
export const periodPlKeys = [
  'feePl',
  'incomePl',
  'tradePl',
  'nftTradePl',
  'nftCapitalPl',
  'realizedPl',
  'airdropPl',
  'totalPl',
] as const
/** 任意精度の期間別損益合計。 */
export type PeriodPlTotals = Record<(typeof periodPlKeys)[number], BigDecimal>
/** 永続化した十進文字列の検証定義。 */
const storedTotalsSchema = z.object({
  feePl: z.string(),
  incomePl: z.string(),
  tradePl: z.string(),
  nftTradePl: z.string(),
  nftCapitalPl: z.string(),
  realizedPl: z.string(),
  airdropPl: z.string(),
  totalPl: z.string(),
})

/** 全損益がゼロの集計状態を返す。 */
export const createPeriodPlTotals = (): PeriodPlTotals => ({
  feePl: createBigDecimal(0n),
  incomePl: createBigDecimal(0n),
  tradePl: createBigDecimal(0n),
  nftTradePl: createBigDecimal(0n),
  nftCapitalPl: createBigDecimal(0n),
  realizedPl: createBigDecimal(0n),
  airdropPl: createBigDecimal(0n),
  totalPl: createBigDecimal(0n),
})
/**
 * 入出金の損益を既存の集計状態へ加算する。未設定値とゼロは加算を省略する。
 * @param params 集計状態と一件の損益。
 */
export const addPeriodPlTransfer = (params: {
  totals: PeriodPlTotals
  transfer: Partial<PeriodPlTotals>
}): void => {
  for (const key of periodPlKeys) {
    const value = params.transfer[key]
    if (value !== undefined && value.value !== 0n)
      params.totals[key] = plus(params.totals[key], value)
  }
}
/** @param params 永続化する合計。 @returns 精度を維持する十進文字列の JSON。 */
export const serializePeriodPlTotals = (params: {
  totals: PeriodPlTotals
}): string =>
  JSON.stringify(
    Object.fromEntries(
      periodPlKeys.map((key) => [key, toStringBigDecimal(params.totals[key])]),
    ),
  )
/** @param params 保存済み JSON。 @returns 検証・復元済み合計。 */
export const parsePeriodPlTotals = (params: {
  text: string
}): PeriodPlTotals => {
  const stored = storedTotalsSchema.parse(JSON.parse(params.text))
  const totals = createPeriodPlTotals()
  for (const key of periodPlKeys) totals[key] = createBigDecimal(stored[key])
  return totals
}
